import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ChooseFood from "assets/illustrations/chooseFoodImage.svg";
import ProcessingOrderImage from "assets/illustrations/processingOrderImage.svg";
import OrderStep2Arrow from "assets/OrderStep2Arrow.svg";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerSubHeaderText } from "components/primitives/DrawerSubHeaderText";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { HeaderText } from "components/primitives/HeaderText";
import { StyledBottomMessage } from "components/primitives/StyledBottomMessage";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useOpenDoorAlarm } from "hooks/useOpenDoorAlarm";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { selectActiveLocalTransaction, selectOrderDishTypes, selectTransactionDishes } from "slices/transactionSlice";
import { TransactionState } from "types";
import { ORDER_STEP_3_SMALL_ORDER_LIMIT } from "utils/designTokens";
import { OpenedDoorWarningBox } from "./components/OpenedDoorWarningBox";
import { OrderDishesList } from "./components/OrderDishesList";
import { OrderDishesWrapper, OrderItemBlockWrapper } from "./components/orderStepDrawerStyles";
import { StyledDishTypeList } from "./components/StyledDishTypeList";
import type { BaseOrderStepProps } from "./OrderStep2";

const Body = styled(Box)(() => ({
	width: "100%",
	maxHeight: "1000px",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	padding: "0 0 120px 0",
	img: {
		marginTop: 16,
	},
}));

const TakeYourMealsHeader = () => {
	const { t } = useTranslation();
	return (
		<>
			<HeaderText variant="h3">{t("order.step3.header")}</HeaderText>
			<OrderStep2Arrow />
			<DrawerSubHeaderText>{t("order.step3.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckHeader = () => {
	const { t } = useTranslation();

	return (
		<>
			<HeaderText variant="h3" hasBorder={true}>
				{t("order.step4.header")}
			</HeaderText>
			<DrawerSubHeaderText>{t("order.step4.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckDrawerMessage = () => {
	const { t } = useTranslation();
	return (
		<StyledBottomMessage data-testid="stock-check">
			<Typography variant="promoHeader">{t("order.step4.header")}...</Typography>
			<CircularProgress sx={{ margin: "auto", color: "#00396c" }} size="75px" thickness={5} />
		</StyledBottomMessage>
	);
};

export const OrderStep3 = ({ open, toggleDrawer }: BaseOrderStepProps) => {
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const transactionState = transaction?.state;
	const isStockCheck = transactionState === TransactionState.StockCheck;
	const { trackStockCheckStarted, trackOrderStep3Screen } = useMixpanelTrackers();
	const { resetCountdown, displayOpenedDoorWarning } = useOpenDoorAlarm(transaction?.type, transactionState);

	useEffect(() => {
		trackOrderStep3Screen();
	}, [trackOrderStep3Screen]);

	// Dismissing the warning/alarm when the transaction leaves Active is owned by useOpenDoorAlarm now
	// (it stops, rather than restarting the countdown). This effect only handles the StockCheck analytics.
	useEffect(() => {
		if (transactionState === TransactionState.StockCheck) {
			trackStockCheckStarted();
		}
	}, [transactionState, trackStockCheckStarted]);

	useEffect(() => {
		if (displayOpenedDoorWarning) {
			toggleDrawer(true);
		}
	}, [displayOpenedDoorWarning, toggleDrawer]);

	return (
		<>
			<SwipeableArea orderstep={3} open={open}>
				{!open && <BlurredBackground orderStep={3} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Box sx={{ width: "100%", display: "flex", flexDirection: "column", paddingTop: 2 }}>
					{isStockCheck ? <StockCheckHeader /> : <TakeYourMealsHeader />}
				</Box>
			</SwipeableArea>
			<Body
				sx={{
					...(open && {
						marginTop: "220px",
					}),
				}}
			>
				<OrderStep3Body displayOpenedDoorWarning={displayOpenedDoorWarning} resetCountdown={resetCountdown} />
			</Body>
		</>
	);
};

interface OrderStep3BodyProps {
	displayOpenedDoorWarning: boolean;
	resetCountdown: () => void;
}

const OrderStep3Body = (props: OrderStep3BodyProps) => {
	const { t } = useTranslation();
	const orderDishes = useAppSelector(selectTransactionDishes);
	const orderDishTypes = useAppSelector(selectOrderDishTypes);
	const transaction = useAppSelector(selectActiveLocalTransaction);
	const transactionState = transaction?.state;
	const { displayOpenedDoorWarning, resetCountdown } = props;

	if (orderDishes.length === 0 && transactionState === TransactionState.Active) {
		return (
			<>
				<ChooseFood />
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</>
		);
	}

	if (orderDishes.length > 0) {
		return (
			<>
				<StyledDishTypeList
					isLargeOrder={orderDishTypes && orderDishTypes?.length > ORDER_STEP_3_SMALL_ORDER_LIMIT}
					data-testid="dish-list"
				>
					<OrderItemBlockWrapper>
						{orderDishTypes && (
							<OrderDishesWrapper>
								<OrderDishesList smallOrderLimit={ORDER_STEP_3_SMALL_ORDER_LIMIT} />
							</OrderDishesWrapper>
						)}
					</OrderItemBlockWrapper>
				</StyledDishTypeList>
				{transactionState === TransactionState.StockCheck && <StockCheckDrawerMessage />}
				{displayOpenedDoorWarning && <OpenedDoorWarningBox resetCountdown={resetCountdown} />}
			</>
		);
	}

	if (transactionState === TransactionState.StockCheck) {
		return (
			<>
				<ProcessingOrderImage
					style={{
						position: "absolute",
						top: "165px",
					}}
				/>
				<StyledBottomMessage data-testid="stock-check">
					<Typography variant="promoHeader">{t("order.step4.header")}...</Typography>
					<CircularProgress sx={{ margin: "auto", color: "#00396c" }} size="75px" thickness={5} />
				</StyledBottomMessage>
			</>
		);
	}
};
